import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { BrowserStorage } from '@atc/browser-storage';

// Formsy

import {
    Alert,
    Box,
    Glyphicon,
    PositionedOverlay,
} from 'reaxl';
import { useAnalytics } from 'reaxl-analytics';
import {
    Form,
} from 'reaxl-forms';

import { ownerEmailSubcribeClick } from '@/analytics/srp/owner/ownerEmailEvents';

import EmailSubscribeAlertForm from './EmailSubscribeAlertForm';
import EmailSubscribeWithoutAlertForm from './EmailSubscribeWithoutAlertForm';

/**
 * The EmailSubscribeForm component is a generic form with confirmation that takes an email and query and processes based on an action passed by the parent.
 * It also will leverage the LocalStorage 'ATC_EMAIL_SUBSCRIBE' item to control the visibility of the container via its 'isClosed' boolean.
 *
 * @component
 * @example
 * // Basic usage
 * <EmailSubscribeForm
 *   onSubmit={handleSubmit}
 *   title="Subscribe for Updates"
 * />
 *
 * // Usage in modal/sheet (not inline)
 * <EmailSubscribeForm
 *   onSubmit={handleSubmit}
 *   isNotInlineForm={true}
 *   registrationSuccess="Successfully subscribed!"
 * />
 *
 * @param {Object} props - The component props
 * @param {string} [props.email=''] - Pre-populated email value
 * @param {Function} [props.emailSubscribeSend] - Callback for email subscription
 * @param {string} [props.registrationError] - Error message to display
 * @param {string} [props.registrationSuccess] - Success message to display
 * @param {string} [props.registrationFailure] - Warning message to display
 * @param {Function} [props.onClose] - Callback when form is closed
 * @param {Function} [props.onSubmit] - Callback when form is submitted
 * @param {Object} [props.user] - User object data
 * @param {string|React.ReactNode} [props.title] - Form title content
 * @param {boolean} [props.withCustomContent=false] - Whether to render with custom styling
 * @param {boolean} [props.isSubscribeAlert=false] - Whether to render as alert form variant
 * @param {boolean} [props.isNotInlineForm=false] - Whether the form is rendered in a modal/sheet context.
 *   When true, applies specific padding styles (padding-top-2, padding-horizontal-0, padding-bottom-0)
 *   and adjusts alert positioning for non-inline usage like modals or sheets.
 * @param {Function} [props.onEmailInputChange] - Callback when email input changes
 *
 * @returns {React.ReactNode} The rendered email subscription form
 */

let Storage;

function EmailSubscribeForm({
    email = '',
    emailSubscribeSend,
    registrationError,
    registrationSuccess,
    registrationFailure,
    onClose,
    onSubmit,
    user,
    title,
    withCustomContent = false,
    isSubscribeAlert = false,
    isNotInlineForm = false,
    onEmailInputChange = () => { },
    ...rest
}) {
    const myForm = useRef(null);

    const { sendClick } = useAnalytics();

    useEffect(() => {
        Storage = new BrowserStorage('emailSubscribe', {
            persist: false,
        });
        const emailSubscribe = Storage.getItem('isClosed') || false;
        if (emailSubscribe && (typeof onClose === 'function')) {
            onClose();
        }
    }, [onClose]);

    const handleClose = () => {
        Storage.setItem('isClosed', false);

        if (typeof onClose === 'function') {
            onClose();
        }
    };

    const handleSubmit = (data) => {
        if (!isSubscribeAlert) {
            myForm.current.reset();
            onSubmit(data);
            sendClick(ownerEmailSubcribeClick);
        } else {
            onSubmit(data);
        }
    };

    const renderCloseButton = () => typeof onClose === 'function' && (
        <Glyphicon
            glyph="remove"
            className="padding-right-2 cursor-pointer"
            onClick={handleClose}
        />
    );

    const panelClassNames = clsx({
        'padding-horizontal-0': isSubscribeAlert,
        'padding-top-0 margin-top-md-2 margin-top-lg-4': withCustomContent,
        'padding-horizontal-0 padding-bottom-0 padding-top-2': isNotInlineForm,
    });

    const alertMap = {
        danger: registrationError,
        success: registrationSuccess,
        warning: registrationFailure,
    };

    const renderAlert = (alertClassNames) => {
        const alertStyle = Object.keys(alertMap).find((key) => alertMap[key]);
        return !!alertStyle && (
            <Alert
                bsStyle={alertStyle}
                className={alertClassNames}
            >
                {alertMap[alertStyle]}
            </Alert>
        );
    };

    const emailFormProps = {
        title,
        user,
        email,
        onEmailInputChange,
        emailSubscribeSend,
        isNotInlineForm,
    };

    return (
        <>
            <PositionedOverlay
                className={isNotInlineForm ? 'w-100' : ''}
                shrinkwrap={false}
            >
                <PositionedOverlay.Base>
                    <Box
                        backgroundColor="neutral-primary-transparent-40"
                        className={panelClassNames}
                        {...rest}
                    >
                        <Form
                            onValidSubmit={handleSubmit}
                            ref={myForm}
                        >
                            {isSubscribeAlert ? (
                                <EmailSubscribeAlertForm
                                    withCustomContent={withCustomContent}
                                    {...emailFormProps}
                                />
                            ) : (<EmailSubscribeWithoutAlertForm {...emailFormProps} />)}
                        </Form>
                        {isSubscribeAlert && renderAlert('margin-horizontal-3')}
                    </Box>
                </PositionedOverlay.Base>
                <PositionedOverlay.Anchor position="topRight">
                    {typeof onClose === 'function' && renderCloseButton()}
                </PositionedOverlay.Anchor>
            </PositionedOverlay>

            {!isSubscribeAlert && renderAlert(isNotInlineForm ? 'margin-top-3 margin-bottom-0' : null)}
        </>
    );
}

EmailSubscribeForm.propTypes /* remove-proptypes */ = {
    /**
    * The className to apply to the root node
    */
    className: PropTypes.string,
    /**
    * The callback to fire on valid submit of subscribe form
    */
    onSubmit: PropTypes.func,
    /**
    * The callback to fire on close of subscribe form if no function is provided, the button will not display
    */
    onClose: PropTypes.func,
    /**
    * The copy to describe the intent of the form
    */
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
    * The copy returning from the registration service in the case or a server error
    */
    registrationError: PropTypes.string,
    /**
    * The copy returning from the registration service for successful registration
    */
    registrationSuccess: PropTypes.string,
    /**
    * Whether the form should render with custom content
    */
    withCustomContent: PropTypes.bool,
};

export default React.memo(EmailSubscribeForm);
